/**
 * The one place that talks to the API.
 *
 * Three things it owns, so no screen has to think about them:
 *
 *  - **The CSRF token.** 17A validates `X-CSRF-TOKEN` on every write. The token is fetched once,
 *    cached, and refetched once automatically when the server says it has expired — a session
 *    that outlives its token is ordinary, and making the user reload to recover from it is not.
 *  - **The error envelope.** Every failure comes back as `{ code, message, traceId, errors? }`
 *    (契约 §3). It arrives here as a typed `ApiFailure`, never as a bare string.
 *  - **Being signed out.** A 401 is not an error a form should render; it means the session is
 *    gone, and the whole app has to react. It throws a distinguishable type for that reason.
 */

export interface ApiErrorBody {
  code: string
  message: string
  traceId?: string | null
  errors?: Record<string, string[]> | null
}

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message)
    this.name = 'ApiFailure'
  }

  get code(): string {
    return this.body.code
  }

  /** Field name → messages, when the failure was model validation. */
  get fieldErrors(): Record<string, string[]> {
    return this.body.errors ?? {}
  }
}

/** The session is gone. Every screen treats this the same way, so it has its own type. */
export class NotSignedIn extends Error {
  constructor() {
    super('Not signed in.')
    this.name = 'NotSignedIn'
  }
}

/** The request never reached the server. Distinct from "the server said no". */
export class NetworkDown extends Error {
  constructor(cause: unknown) {
    super('The request did not reach the server.')
    this.name = 'NetworkDown'
    this.cause = cause
  }
}

const CSRF_HEADER = 'X-CSRF-TOKEN'
const CSRF_EXPIRED = 'CSRF_TOKEN_INVALID'
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

let csrfToken: string | null = null
let csrfInFlight: Promise<string> | null = null

async function fetchCsrfToken(): Promise<string> {
  const response = await fetch('/api/antiforgery/token', { credentials: 'same-origin' })

  if (!response.ok) {
    throw new Error(`Could not obtain a CSRF token (${response.status}).`)
  }

  const body = (await response.json()) as { token: string }

  return body.token
}

async function currentCsrfToken(): Promise<string> {
  if (csrfToken) {
    return csrfToken
  }

  // Several writes can start at once on a form-heavy screen; they should share one request.
  csrfInFlight ??= fetchCsrfToken()
    .then((token) => {
      csrfToken = token
      return token
    })
    .finally(() => {
      csrfInFlight = null
    })

  return csrfInFlight
}

/** Signing in or out changes the token the server expects. */
export function forgetCsrfToken(): void {
  csrfToken = null
}

export interface RequestOptions {
  method?: string
  /** A plain object is sent as JSON; FormData is sent as-is so the browser sets the boundary. */
  body?: unknown
  signal?: AbortSignal
  /** For Idempotency-Key, which 契约 §10.1 puts in a header rather than the body. */
  headers?: Record<string, string>
}

async function send(path: string, options: RequestOptions, retryOnCsrf: boolean): Promise<Response> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {}
  let body: BodyInit | undefined

  if (options.body instanceof FormData) {
    body = options.body
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }

  for (const [name, value] of Object.entries(options.headers ?? {})) {
    headers[name] = value
  }

  if (!SAFE_METHODS.has(method)) {
    headers[CSRF_HEADER] = await currentCsrfToken()
  }

  let response: Response

  try {
    response = await fetch(path, {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal: options.signal,
    })
  } catch (cause) {
    throw new NetworkDown(cause)
  }

  if (response.status === 400 && retryOnCsrf && !SAFE_METHODS.has(method)) {
    const failure = await readFailure(response)

    if (failure.code === CSRF_EXPIRED) {
      // One retry with a fresh token. A session outliving its token is ordinary; making someone
      // reload the page to recover from it is not.
      forgetCsrfToken()
      return send(path, options, false)
    }

    throw new ApiFailure(response.status, failure)
  }

  return response
}

async function readFailure(response: Response): Promise<ApiErrorBody> {
  try {
    const body = (await response.json()) as Partial<ApiErrorBody>

    if (typeof body?.code === 'string' && typeof body?.message === 'string') {
      return body as ApiErrorBody
    }
  } catch {
    // Fall through: a body that is not the envelope tells us nothing useful.
  }

  return {
    code: `HTTP_${response.status}`,
    message: `The server returned ${response.status}.`,
  }
}

async function handle<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    throw new NotSignedIn()
  }

  if (!response.ok) {
    throw new ApiFailure(response.status, await readFailure(response))
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return handle<T>(await send(path, { signal }, false))
}

export async function apiSend<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  return handle<T>(await send(path, { method, body, signal }, true))
}

/**
 * A write that carries extra headers. The only caller is order creation, whose idempotency key
 * travels in `Idempotency-Key` — it is a transport-level retry concern, not a property of the
 * order, which is why 契约 §10.1 puts it there and not in the body.
 */
export async function apiSendWithHeaders<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  headers: Record<string, string>,
): Promise<T> {
  return handle<T>(await send(path, { method, body, headers }, true))
}

/** Only for the photo upload, which is multipart rather than JSON. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  return handle<T>(await send(path, { method: 'POST', body: form }, true))
}
